import {
  ArrowUturnLeftIcon,
  Button,
  ContentReferences,
  DateOrTime,
  DevDetailsButton,
  Dialog,
  DialogProps,
  FileDrop,
  HelpHover,
  IconButton,
  InputContainer,
  LoaderCover,
  PencilSquareIcon,
  ReadOnlyProperty,
  Text,
  TextField,
  TrashIcon,
  UserAvatar,
} from 'components'
import { useCurrentUser, useWorkspace } from 'components/providers'
import humanizeString from 'humanize-string'
import { useEffect, useRef, useState } from 'react'
import { IMedia, MediaType } from 'types'
import {
  acceptedImages,
  acceptedPDF,
  acceptedVideos,
  createDoc,
  createImgSrc,
  getImagePath,
  getPdfPath,
  getStoragePath,
  getThumbPath,
  getVideoPath,
  imageFromVideo,
  removeExtension,
  sleep,
  typeFromFile,
  updateDoc,
  updateMedia,
  uploadFile,
  uploadMedia,
  useMedia,
  useStorageUrl,
  useUser,
} from 'utils'
import { VideoPlayer, VideoPlayerClass } from '.'
import Flex from '../Flex'
import Image from '../Image'
import Spacer from '../Spacer'
import PDFViewer from './PDFViewer'

export interface Props extends Omit<DialogProps, 'children' | 'title'> {
  data?: IMedia
  defaultImage?: string
  /** Update the media file */
  newMedia?: File
  onDelete?: () => Promise<boolean>
  onSave?: (doc: IMedia) => void
  type?: MediaType
}

interface NewMedia {
  image?: FileAndUrl
  pdf?: FileAndUrl
  video?: FileAndUrl
}

interface FileAndUrl {
  file: File
  url: string
}

export default function MediaDialog({
  newMedia: controlledNewMedia,
  data,
  defaultImage,
  onClose,
  onDelete,
  onSave,
  type: controlledType,
  ...rest
}: Props) {
  const mediaType =
    data?.type || controlledType || (controlledNewMedia && typeFromFile(controlledNewMedia))
  const { desc, id, title = '', type, updatedBy } = data || {}

  const user = useCurrentUser()
  const updatedByUser = useUser(updatedBy)
  const { workspace } = useWorkspace()
  const { refetch: mediaRefetch } = useMedia(id)

  const { url: imageUrl, refetch: imageRefetch } = useStorageUrl(
    id && mediaType !== 'pdf' ? getImagePath(id) : undefined
  )
  const { url: videoUrl } = useStorageUrl(id && type === 'video' ? getVideoPath(id) : undefined)
  const { refetch: thumbUrlRefetch } = useStorageUrl(
    id && mediaType !== 'pdf' ? getThumbPath(id) : undefined
  )
  const { url: pdfUrl } = useStorageUrl(id && mediaType === 'pdf' ? getPdfPath(id) : undefined)

  const [draftTitle, setDraftTitle] = useState('')
  const [draftDesc, setDraftDesc] = useState('')
  const [progress, setProgress] = useState(0)
  const [newMedia, setNewMedia] = useState<NewMedia>()
  const [videoPlayed, setVideoPlayed] = useState(false)

  const videoPlayerRef = useRef<VideoPlayerClass>(null)

  const pdfSource = newMedia?.pdf?.url || pdfUrl
  const imageSrc = newMedia?.image?.url || imageUrl || defaultImage
  const videoSrc = newMedia?.video?.url || videoUrl
  const madeChange = draftTitle !== title || draftDesc !== (desc || '') || newMedia

  useEffect(() => {
    if (controlledNewMedia) {
      handleFiles([controlledNewMedia])
      setDraftTitle(humanizeString(removeExtension(controlledNewMedia.name)))
    } else {
      setDraftTitle(title)
      setDraftDesc(desc || '')
    }
  }, [controlledNewMedia, title, desc])

  async function handleDone() {
    if (!madeChange) return handleClose()

    if (mediaType === 'pdf') {
      await handlePdfSave()
    } else {
      await handleImageVideoSave()
    }
  }

  async function handlePdfSave() {
    let docId = data?.id
    let doc: IMedia

    if (docId) {
      doc = await updateDoc(user!.id, 'Media', { id: docId, title: draftTitle, desc: draftDesc })
    } else {
      doc = await createDoc(user!.id, 'Media', {
        title: draftTitle,
        desc: draftDesc,
        type: 'pdf',
        workspaceId: workspace!.id,
        dimensions: { height: 0, width: 0 },
      })
      docId = doc.id
    }

    if (newMedia?.pdf) {
      const filePath = `Workspaces/${workspace!.id}/media/pdfs/${docId}`
      await uploadFile(filePath, newMedia.pdf.file, setProgress)
    }

    mediaRefetch()
    onSave?.(doc)
    handleClose()
  }

  async function handleImageVideoSave() {
    let body = { ...data, title: draftTitle, desc: draftDesc }
    let docId = data?.id ?? ''

    if (data) {
      if (newMedia?.video) {
        await updateMedia(workspace!.id, newMedia.video.file, id!, setProgress)
      }

      if (newMedia?.image) {
        const dimensions = await updateMedia(workspace!.id, newMedia.image.file, id!, setProgress)
        body = { ...body, dimensions }
        imageRefetch()
        sleep(3000).then(() => thumbUrlRefetch()) // HACK: wait for the thumb to be generated
      }
    } else {
      if (newMedia?.video) {
        const doc = await uploadMedia(
          workspace!.id,
          user!.id,
          newMedia.video.file,
          setProgress,
          body
        )
        docId = doc?.id

        if (!newMedia.image) {
          const coverImage = await imageFromVideo(newMedia.video.file)
          if (!coverImage) throw 'Error creating thumbnail from video'

          const dimensions = await updateMedia(workspace!.id, coverImage, docId, setProgress)
          body = { ...body, dimensions }
        }
      }

      if (newMedia?.image) {
        if (docId) {
          const dimensions = await updateMedia(
            workspace!.id,
            newMedia.image.file,
            docId,
            setProgress
          )
          body = { ...body, dimensions }
        } else {
          const doc = await uploadMedia(
            workspace!.id,
            user!.id,
            newMedia.image.file,
            setProgress,
            body
          )
          onSave?.(doc)
          handleClose()
          return
        }
      }
    }

    const update = await updateDoc(user!.id, 'Media', { id: docId, ...body })
    mediaRefetch()
    onSave?.(update)
    handleClose()
  }

  async function handleFiles(files: File[]) {
    const file = files[0]

    if (mediaType === 'pdf') {
      setNewMedia({ ...newMedia, pdf: { file, url: URL.createObjectURL(file) } })
    } else {
      const fileType = typeFromFile(file)
      const imageFile = fileType === 'image' ? file : await imageFromVideo(file)

      let update = { ...newMedia }
      if (fileType === 'video') update.video = { file, url: URL.createObjectURL(file) }
      if (imageFile && (fileType === 'image' || type !== 'video'))
        update.image = { file: imageFile, url: await createImgSrc(imageFile) }

      resetVideo()
      setNewMedia(update)
    }
  }

  function resetVideo() {
    videoPlayerRef.current?.showPreview?.()
    setVideoPlayed(false)
  }

  function handleClose() {
    onClose?.()
    setNewMedia(undefined)
    setDraftTitle(title)
    setDraftDesc(desc || '')
    setProgress(0)
  }

  async function handleDelete() {
    const deleted = await onDelete?.()
    if (deleted) handleClose()
  }

  function renderPreview() {
    if (mediaType === 'pdf') {
      return pdfSource ? <PDFViewer file={pdfSource} /> : null
    }

    return videoSrc ? (
      <>
        <VideoPlayer
          ref={videoPlayerRef}
          {...fullHW}
          src={videoSrc}
          onPlay={() => setVideoPlayed(true)}
          playing
          cover={imageSrc}
          controls
        />
        {videoPlayed && (
          <Button
            position="absolute"
            top={2}
            right={2}
            size="sm"
            leftIcon={<ArrowUturnLeftIcon size="sm" />}
            onClick={resetVideo}
          >
            Cover
          </Button>
        )}
      </>
    ) : (
      <Image {...fullHW} draggable={false} objectFit="contain" src={imageSrc} />
    )
  }

  function getAcceptTypes() {
    if (mediaType === 'pdf') return acceptedPDF

    if (type) return { ...acceptedImages, ...(type === 'video' && acceptedVideos) }
    if (newMedia) return { ...acceptedImages, ...(newMedia.video && acceptedVideos) }

    return {
      ...(controlledType !== 'video' && acceptedImages),
      ...(controlledType !== 'image' && acceptedVideos),
    }
  }

  function getReplaceButtonText() {
    if (mediaType === 'pdf') {
      return data || newMedia?.pdf ? 'Replace' : 'Add'
    }
    return data || newMedia ? 'Replace' : 'Add'
  }

  if (!mediaType) return null

  return (
    <Dialog
      placement="center"
      title={
        <Flex gap={2} alignItems="center" justifyContent="space-between">
          <Text>Manage Media</Text>

          {data && (
            <DevDetailsButton
              items={[
                { collection: 'Media', id, label: 'Media', type: 'db' },
                { label: 'Storage', type: 'storage', url: getStoragePath(data) },
              ]}
            />
          )}
        </Flex>
      }
      closeOnInteractOutside={false}
      bodyProps={{ display: 'flex', mb: 6, px: 6, overflow: 'hidden' }}
      contentProps={{ width: '800px', maxWidth: '80%', maxHeight: '80%', height: '700px' }}
      onClose={handleClose}
      {...rest}
    >
      <LoaderCover
        overflow="hidden"
        display="flex"
        flex={1}
        show={progress > 0}
        progress={progress}
      >
        <FileDrop
          display="flex"
          flex={1}
          overflow="hidden"
          onDrop={handleFiles}
          options={{ accept: getAcceptTypes() }}
          alignItems="stretch"
        >
          {({ open }) => (
            <Flex direction="column" overflow="hidden" flex={1} gap={4}>
              <Flex overflow="auto" gap={4} flex={1} flexDirection={{ base: 'column', md: 'row' }}>
                <Flex
                  flex={{ md: 2 }}
                  minHeight={{ base: '300px', md: 'unset' }}
                  justify="center"
                  background="gray.100"
                  overflow="hidden"
                  position="relative"
                >
                  {renderPreview()}
                </Flex>

                <Flex
                  direction="column"
                  overflow={{ base: 'unset', md: 'auto' }}
                  minWidth="250px"
                  gap={4}
                  flex={{ md: 1 }}
                >
                  <TextField
                    label="Name"
                    value={draftTitle}
                    onChange={e => setDraftTitle(e.target.value)}
                  />

                  <TextField
                    label="Description"
                    isArea
                    value={draftDesc}
                    placeholder='Media information and how it should be used (e.g., "Use this image for the project banner")'
                    onChange={e => setDraftDesc(e.target.value)}
                  />

                  {data && (
                    <>
                      <InputContainer label="Update by">
                        <Flex align="center" gap={2}>
                          <UserAvatar user={updatedByUser} />
                          <Text>{updatedByUser?.displayName}</Text>
                        </Flex>
                      </InputContainer>

                      <ReadOnlyProperty
                        label="Updated"
                        value={<DateOrTime>{data.updated}</DateOrTime>}
                      />

                      {data.dimensions && (
                        <ReadOnlyProperty
                          label="Dimensions"
                          value={`${data.dimensions.width} × ${data.dimensions.height}`}
                        />
                      )}

                      <ContentReferences id={data.id} type="media" />
                    </>
                  )}
                </Flex>
              </Flex>

              <Flex align="center" gap={2}>
                {data && onDelete && (
                  <IconButton
                    variant="outline"
                    palette="danger"
                    aria-label="Delete"
                    icon={<TrashIcon />}
                    onClick={handleDelete}
                  />
                )}
                <Button variant="outline" leftIcon={<PencilSquareIcon />} onClick={open}>
                  {getReplaceButtonText()}
                </Button>
                {videoSrc && (
                  <HelpHover text="Select (or drag) video to replace video or image to replace cover" />
                )}
                <Spacer />
                {madeChange && (
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                )}
                <Button palette="primary" onClick={handleDone}>
                  {madeChange ? 'Save' : 'Done'}
                </Button>
              </Flex>
            </Flex>
          )}
        </FileDrop>
      </LoaderCover>
    </Dialog>
  )
}

const fullHW = { height: '100%', width: '100%' }
